import * as React from 'react'

type UseRovingGridOptions = {
  /**
   * Total number of focusable items in the grid. Changing this resets the
   * active index to 0, since a refiltered grid (e.g. after a search) has no
   * meaningful relationship between old and new positions.
   */
  itemCount: number
  /**
   * Number of items per row. Used to translate vertical arrow keys into an
   * index delta and to compute the current column for wrapping.
   */
  columns: number
  /**
   * When true, navigation wraps at the edges: moving past the last item lands
   * on the first (and vice versa), and vertical moves wrap within the same
   * column. When false (the default), edge moves are a no-op that keeps the
   * current item.
   */
  loop?: boolean
  /**
   * Writing direction of the grid. In `'rtl'` the horizontal arrows are
   * mirrored (ArrowLeft moves forward, ArrowRight moves back) to match the
   * visual order. Vertical arrows and Home/End are unaffected. Defaults to
   * `'ltr'`.
   */
  dir?: 'ltr' | 'rtl'
}

type UseRovingGridResult = {
  /** Index of the item that currently owns `tabIndex={0}` (the roving tabstop). */
  activeIndex: number
  /**
   * Imperatively set the active item, e.g. on pointer focus. The index is
   * clamped into the valid range so callers never have to bounds-check.
   */
  setActiveIndex: (index: number) => void
  /** Keydown handler to wire onto the grid container for arrow/Home/End nav. */
  onKeyDown: (event: React.KeyboardEvent) => void
}

const NAVIGATION_KEYS = new Set([
  'ArrowRight',
  'ArrowLeft',
  'ArrowDown',
  'ArrowUp',
  'Home',
  'End',
])

function clamp(index: number, min: number, max: number): number {
  if (index < min) return min
  if (index > max) return max
  return index
}

type RovingGridGeometry = Pick<
  UseRovingGridOptions,
  'itemCount' | 'columns'
> & {
  loop?: boolean
  dir?: 'ltr' | 'rtl'
}

/**
 * Pure resolver for "given the current index and a navigation key, which index
 * should become active?" Shared by {@link useRovingGrid} and its docs demo so
 * the two can never disagree about where a key lands. Returns `current`
 * unchanged for non-navigation keys, an empty grid, or an edge move without
 * `loop`.
 */
function getRovingGridDestination(
  key: string,
  current: number,
  { itemCount, columns, loop = false, dir = 'ltr' }: RovingGridGeometry,
): number {
  if (itemCount === 0) return current

  const last = itemCount - 1
  const col = current % columns
  // In RTL the visual "forward" arrow is ArrowLeft, so resolve each horizontal
  // key to a direction-agnostic delta before applying it.
  const forwardKey = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight'
  const backKey = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft'

  if (key === forwardKey) {
    return current === last ? (loop ? 0 : last) : current + 1
  }
  if (key === backKey) {
    return current === 0 ? (loop ? last : 0) : current - 1
  }

  switch (key) {
    // Vertical moves stay in the same column. Off the edge, they either wrap to
    // the opposite end of the column (loop) or no-op.
    case 'ArrowDown': {
      const next = current + columns
      return next <= last ? next : loop ? col : current
    }
    case 'ArrowUp': {
      const prev = current - columns
      // Wrapping upward lands on the deepest item still within the grid for
      // this column: col + columns * floor((last - col) / columns).
      return prev >= 0
        ? prev
        : loop
          ? col + columns * Math.floor((last - col) / columns)
          : current
    }
    case 'Home':
      return 0
    case 'End':
      return last
    default:
      return current
  }
}

/**
 * Roving-tabindex state machine for a 2-D grid. Framework-only: it owns the
 * active index and translates keyboard navigation into index moves, without
 * any DOM or focus side effects. Pair it with `tabIndex` and focus management
 * in the rendering component.
 *
 * Supports optional wrapping (`loop`) and right-to-left grids (`dir`), which
 * mirror the horizontal arrows while leaving vertical and Home/End moves
 * direction-independent.
 */
function useRovingGrid({
  itemCount,
  columns,
  loop = false,
  dir = 'ltr',
}: UseRovingGridOptions): UseRovingGridResult {
  const [activeIndex, setActiveIndexState] = React.useState(0)

  // A refiltered grid invalidates the previous position; start over at 0.
  React.useEffect(() => {
    setActiveIndexState(0)
  }, [itemCount])

  const setActiveIndex = React.useCallback(
    (index: number) => {
      // Guard the empty grid: max would be -1 and clamp against 0 as the min.
      setActiveIndexState(itemCount === 0 ? 0 : clamp(index, 0, itemCount - 1))
    },
    [itemCount],
  )

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (!NAVIGATION_KEYS.has(event.key)) return

      event.preventDefault()
      setActiveIndexState((current) =>
        getRovingGridDestination(event.key, current, {
          itemCount,
          columns,
          loop,
          dir,
        }),
      )
    },
    [itemCount, columns, loop, dir],
  )

  return { activeIndex, setActiveIndex, onKeyDown }
}

export { useRovingGrid, getRovingGridDestination }
export type { UseRovingGridOptions, UseRovingGridResult, RovingGridGeometry }
